import { useEffect, useMemo, useRef, useState } from 'react';
import type { IcebreakerCard } from '../lib/content';
import { dealOrder, newSeed, readSeed, writeSeed } from '../lib/deck';
import { IconBack, IconForward } from './Icon';

/**
 * The card deck itself, without a page around it (D62).
 *
 * **A component rather than a route, because it is rendered in three places.**
 * Its own route, the Schedule's desktop rail where it replaced "Coming in", and
 * the Schedule's phone switcher. One implementation, so the three cannot
 * disagree about what card three is — and the seed lives in `sessionStorage`
 * (`lib/deck`) rather than in this component's state, so they do not disagree
 * across a navigation either.
 *
 * **It owns the index; the URL follows.** The first version derived the index
 * from the route parameter, which cannot work once the deck is embedded in a
 * page that has no parameter of its own. So the deck holds the position and
 * tells its caller through `onCardChange`; the route turns that into a
 * `replace` navigation, and the Schedule ignores it. Doing it the other way
 * round — an effect syncing state back from a prop — is the loop this avoids.
 *
 * `variant` is the only thing that differs between the three. `rail` is a
 * 360px column beside the schedule on a laptop, where the type has to come down
 * and the intro has no room; `page` is the full-height card.
 */
export default function IcebreakerDeck({
  cards,
  entryId,
  onCardChange,
  variant = 'page',
}: {
  cards: IcebreakerCard[];
  /** A card named by a link, dealt first. Read once, at mount. */
  entryId?: string;
  /** Called when the visible card changes, so a route can mirror it in the URL. */
  onCardChange?: (id: string | null) => void;
  variant?: 'page' | 'rail';
}) {
  const [seed, setSeed] = useState(readSeed);
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);

  /**
   * Frozen at mount on purpose. `entryId` comes from the route parameter, which
   * this component's own `onCardChange` rewrites on every step — feeding the
   * live value back into `dealOrder` would re-pin whatever card you just moved
   * to and the deck would never advance past it.
   *
   * Sound for both ways in that exist: an external link mounts the component
   * fresh, and paging never touches `pinned`. The latent trap is an in-app
   * `<Link>` to a *different* `/icebreakers/:id` from this same page — React
   * Router does not remount on a parameter-only change, so the second id would
   * be silently ignored. No such link exists; if one is ever added, the fix is
   * `key={cardId}` on this component in `routes/Icebreakers.tsx`.
   */
  const [pinned] = useState(entryId);
  const order = useMemo(() => dealOrder(cards, seed, pinned), [cards, seed, pinned]);

  /**
   * Clamped inline rather than corrected by an effect.
   *
   * An organiser deleting a card while somebody is on the last one leaves the
   * index past the end. The first version fixed that in a `useEffect`, which
   * works but paints one frame of the empty placeholder first — the invalid
   * index is rendered and then corrected. Deriving the shown index means it is
   * never invalid in the first place.
   */
  const shown = order.length === 0 ? 0 : Math.min(index, order.length - 1);
  const card = order[shown];
  const last = shown === order.length - 1;

  const go = useMemo(() => (delta: number) => {
    setIndex((at) => {
      // From the clamped position, so stepping back from a deck that shrank
      // goes where the guest can see they are rather than from a stale index.
      const from = order.length === 0 ? 0 : Math.min(at, order.length - 1);
      const next = from + delta;
      if (next < 0 || next >= order.length) return from;
      onCardChange?.(order[next].id);
      return next;
    });
  }, [order, onCardChange]);

  const reshuffle = () => {
    const fresh = newSeed();
    writeSeed(fresh);
    setSeed(fresh);
    setIndex(0);
    onCardChange?.(null);
  };

  /**
   * Arrow keys, but **only when the deck is the page**.
   *
   * The binding is on `window`, which was right when this lived on a route of
   * its own and is not right in the rail: there the deck is a column beside the
   * schedule, and a global listener would have it answering arrow keys for the
   * whole event week on a page somebody opened to read the programme. The
   * buttons are still there, and on the phone — where the deck takes the whole
   * panel — `variant` is `page` and the keys work.
   */
  useEffect(() => {
    if (variant !== 'page') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, variant]);

  if (!card) return <div className={`ice-card ice-card-loading ice-${variant}`} />;

  return (
    <div className={`ice-deck ice-${variant}`}>
      <div
        className="ice-stage"
        onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStart.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStart.current;
          // Same threshold as the photo viewer, so a swipe means the same thing
          // in both places on the site.
          if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
          touchStart.current = null;
        }}
      >
        {/* `key` on the card so React replaces the node rather than editing it,
            which is what lets the entry animation run on every card. */}
        <article className="ice-card" key={card.id}>
          {card.kind === 'game' && <span className="ice-kind">Needs a volunteer</span>}
          {/* Announced rather than silently swapped: without this a screen
              reader is told nothing at all when the card changes, because
              nothing focusable moved. */}
          <p className="ice-text" aria-live="polite">{card.text}</p>
        </article>
      </div>

      <div className="ice-nav">
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          onClick={() => go(-1)}
          disabled={shown === 0}
          aria-label="Previous card"
        >
          <IconBack />
        </button>

        <span className="ice-count">{shown + 1} of {order.length}</span>

        {last ? (
          <button type="button" className="btn btn-primary" onClick={reshuffle}>
            Shuffle again
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            onClick={() => go(1)}
            aria-label="Next card"
          >
            <IconForward />
          </button>
        )}
      </div>

      {last && <p className="ice-done">That's all {order.length} of them. Sorry.</p>}
    </div>
  );
}
